import { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  Pagination,
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
} from 'recharts';
import { Search, Download } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import {
  bookingReportApi,
  roomApi,
  ReportSummary,
  ReportDetailed,
  ReportByRoom,
  Room,
} from '../../services/hotelApi';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const COLORS = ['#0D3C4C', '#1A5F7A', '#ff9800', '#4caf50', '#f44336', '#9c27b0', '#2196f3'];

const Reports = () => {
  const { language } = useLanguage();
  const { user } = useAuth();
  const [tabValue, setTabValue] = useState(0);

  // Common filters
  const now = new Date();
  const firstOfMonth = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
  const today = now.toISOString().split('T')[0];
  
  const [startDate, setStartDate] = useState(firstOfMonth);
  const [endDate, setEndDate] = useState(today);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterRoom, setFilterRoom] = useState('');
  const [page, setPage] = useState(1);

  // Data
  const [summary, setSummary] = useState<ReportSummary | null>(null);
  const [detailed, setDetailed] = useState<ReportDetailed | null>(null);
  const [byRoom, setByRoom] = useState<ReportByRoom[]>([]);
  const [rooms, setRooms] = useState<Room[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const currency = user?.company?.signCurrency || '$';

  const t = language === 'fr' ? {
    title: 'Rapports',
    subtitle: 'Analyses et statistiques des réservations',
    summary: 'Résumé',
    detailed: 'Détaillé',
    byRoom: 'Par Chambre',
    startDate: 'Date début',
    endDate: 'Date fin',
    status: 'Statut',
    room: 'Chambre',
    all: 'Tous',
    search: 'Rechercher',
    totalBookings: 'Total Réservations',
    totalAmount: 'Montant Total',
    totalPaid: 'Total Payé',
    totalRemaining: 'Total Restant',
    average: 'Moyenne',
    byStatus: 'Par Statut',
    byDay: 'Revenus par Jour',
    byRateType: 'Par Type de Tarif',
    byPaymentMethod: 'Par Méthode de Paiement',
    client: 'Client',
    wife: 'Nom Femme',
    type: 'Type',
    checkIn: 'Check-in',
    checkOut: 'Check-out',
    duration: 'Durée',
    amount: 'Montant',
    paid: 'Payé',
    remaining: 'Reste',
    paymentStatus: 'Statut Paiement',
    method: 'Méthode',
    roomNumber: 'Chambre',
    roomType: 'Type',
    nbBookings: 'Nb Réservations',
    totalRevenue: 'Revenu Total',
    avgAmount: 'Moyenne',
    noData: 'Aucune donnée',
    loading: 'Chargement...',
    deletedRoom: 'Chambre supprimée',
    pending: 'En attente',
    confirmed: 'Confirmé',
    checked_in: 'Enregistré',
    checked_out: 'Sorti',
    cancelled: 'Annulé',
    paidStatus: 'Payé',
    partial: 'Partiel',
    unpaid: 'Impayé',
    noAmount: 'Sans montant',
    standard: 'Standard',
    negotiated: 'Négocié',
    cash: 'Espèces',
    mobile_money: 'Mobile Money',
    bank_transfer: 'Virement',
    card: 'Carte',
    count: 'Nombre',
    revenue: 'Revenu',
  } : {
    title: 'Reports',
    subtitle: 'Booking analytics and statistics',
    summary: 'Summary',
    detailed: 'Detailed',
    byRoom: 'By Room',
    startDate: 'Start Date',
    endDate: 'End Date',
    status: 'Status',
    room: 'Room',
    all: 'All',
    search: 'Search',
    totalBookings: 'Total Bookings',
    totalAmount: 'Total Amount',
    totalPaid: 'Total Paid',
    totalRemaining: 'Total Remaining',
    average: 'Average',
    byStatus: 'By Status',
    byDay: 'Revenue by Day',
    byRateType: 'By Rate Type',
    byPaymentMethod: 'By Payment Method',
    client: 'Client',
    wife: 'Wife Name',
    type: 'Type',
    checkIn: 'Check-in',
    checkOut: 'Check-out',
    duration: 'Duration',
    amount: 'Amount',
    paid: 'Paid',
    remaining: 'Remaining',
    paymentStatus: 'Payment Status',
    method: 'Method',
    roomNumber: 'Room',
    roomType: 'Type',
    nbBookings: '# Bookings',
    totalRevenue: 'Total Revenue',
    avgAmount: 'Average',
    noData: 'No data',
    loading: 'Loading...',
    deletedRoom: 'Deleted Room',
    pending: 'Pending',
    confirmed: 'Confirmed',
    checked_in: 'Checked In',
    checked_out: 'Checked Out',
    cancelled: 'Cancelled',
    paidStatus: 'Paid',
    partial: 'Partial',
    unpaid: 'Unpaid',
    noAmount: 'No Amount',
    standard: 'Standard',
    negotiated: 'Negotiated',
    cash: 'Cash',
    mobile_money: 'Mobile Money',
    bank_transfer: 'Bank Transfer',
    card: 'Card',
    count: 'Count',
    revenue: 'Revenue',
  };

  useEffect(() => {
    roomApi.getAll().then(data => setRooms(Array.isArray(data) ? data : [])).catch(() => {});
  }, []);

  const handleSearch = () => {
    if (tabValue === 0) loadSummary();
    else if (tabValue === 1) loadDetailed();
    else loadByRoom();
  };

  const loadSummary = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await bookingReportApi.getSummary({
        startDate,
        endDate,
        status: filterStatus || undefined,
      });
      setSummary(data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadDetailed = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await bookingReportApi.getDetailed({
        startDate,
        endDate,
        status: filterStatus || undefined,
        roomId: filterRoom || undefined,
        page,
        limit: 50,
      });
      setDetailed(data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadByRoom = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await bookingReportApi.getByRoom({ startDate, endDate });
      setByRoom(Array.isArray(data) ? data : []);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    handleSearch();
  }, [tabValue]);

  useEffect(() => {
    if (tabValue === 1) loadDetailed();
  }, [page]);

  const getPaymentStatusColor = (status: string) => {
    const map: Record<string, string> = {
      paid: '#4caf50',
      partial: '#ff9800',
      unpaid: '#f44336',
      'no-amount': '#9e9e9e',
    };
    return map[status] || '#9e9e9e';
  };

  const getPaymentStatusLabel = (status: string) => {
    const map: Record<string, string> = {
      paid: t.paidStatus,
      partial: t.partial,
      unpaid: t.unpaid,
      'no-amount': t.noAmount,
    };
    return map[status] || status;
  };

  const translateStatus = (status: string) => {
    const labels: Record<string, string> = {
      pending: t.pending,
      confirmed: t.confirmed,
      checked_in: t.checked_in,
      checked_out: t.checked_out,
      cancelled: t.cancelled,
    };
    return labels[status] || status;
  };

  const formatPaymentMethod = (method: string) => {
    const map: Record<string, string> = {
      cash: t.cash,
      mobile_money: t.mobile_money,
      bank_transfer: t.bank_transfer,
      card: t.card,
    };
    return map[method] || method || '-';
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: '#0D3C4C' }}>{t.title}</Typography>
        <Typography variant="body1" color="text.secondary">{t.subtitle}</Typography>
      </Box>

      {/* Tabs */}
      <Tabs
        value={tabValue}
        onChange={(_, v) => setTabValue(v)}
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label={t.summary} sx={{ textTransform: 'none' }} />
        <Tab label={t.detailed} sx={{ textTransform: 'none' }} />
        <Tab label={t.byRoom} sx={{ textTransform: 'none' }} />
      </Tabs>

      {/* Filters */}
      <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                label={t.startDate}
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                label={t.endDate}
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              <FormControl fullWidth size="small">
                <InputLabel>{t.status}</InputLabel>
                <Select value={filterStatus} label={t.status} onChange={(e) => setFilterStatus(e.target.value)}>
                  <MenuItem value="">{t.all}</MenuItem>
                  <MenuItem value="pending">{t.pending}</MenuItem>
                  <MenuItem value="confirmed">{t.confirmed}</MenuItem>
                  <MenuItem value="checked_in">{t.checked_in}</MenuItem>
                  <MenuItem value="checked_out">{t.checked_out}</MenuItem>
                  <MenuItem value="cancelled">{t.cancelled}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {tabValue === 1 && (
              <Grid item xs={12} sm={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>{t.room}</InputLabel>
                  <Select value={filterRoom} label={t.room} onChange={(e) => setFilterRoom(e.target.value)}>
                    <MenuItem value="">{t.all}</MenuItem>
                    {rooms.map(room => (
                      <MenuItem key={room._id} value={room._id}>{room.roomNumber}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
            <Grid item xs={12} sm={2}>
              <Button
                variant="contained"
                fullWidth
                startIcon={<Search size={16} />}
                onClick={handleSearch}
                sx={{
                  backgroundColor: '#0D3C4C',
                  textTransform: 'none',
                  '&:hover': { backgroundColor: '#164B61' },
                }}
              >
                {t.search}
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {/* TAB 0 - Summary */}
      <TabPanel value={tabValue} index={0}>
        {!loading && summary && (
          <>
            {/* Overview cards */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
              {[
                { label: t.totalBookings, value: summary.totalBookings || 0, color: '#0D3C4C' },
                { label: t.totalAmount, value: `${(summary.totalFinalAmount || 0).toLocaleString()} ${currency}`, color: '#1A5F7A' },
                { label: t.totalPaid, value: `${(summary.totalPaidAmount || 0).toLocaleString()} ${currency}`, color: '#4caf50' },
                { label: t.totalRemaining, value: `${(summary.totalRemainingAmount || 0).toLocaleString()} ${currency}`, color: '#f44336' },
                { label: t.average, value: `${(summary.averageAmount || 0).toLocaleString()} ${currency}`, color: '#ff9800' },
              ].map((card, idx) => (
                <Grid item xs={12} sm={6} md={2.4} key={idx}>
                  <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                    <CardContent sx={{ textAlign: 'center', py: 2 }}>
                      <Typography variant="h5" sx={{ fontWeight: 700, color: card.color }}>{card.value}</Typography>
                      <Typography variant="caption" color="text.secondary">{card.label}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            {/* Charts Row 1 */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} md={6}>
                <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>{t.byStatus}</Typography>
                    <ResponsiveContainer width="100%" height={280}>
                      <PieChart>
                        <Pie
                          data={(summary.bookingsByStatus || []).map(item => ({
                            name: translateStatus(item._id),
                            value: item.count,
                          }))}
                          cx="50%"
                          cy="50%"
                          outerRadius={90}
                          dataKey="value"
                          label={({ name, value }) => `${name}: ${value}`}
                        >
                          {(summary.bookingsByStatus || []).map((_, index) => (
                            <Cell key={index} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>{t.byRateType}</Typography>
                    <ResponsiveContainer width="100%" height={280}>
                      <BarChart data={(summary.bookingsByRateType || []).map(item => ({
                        name: item._id === 'standard' ? t.standard : t.negotiated,
                        [t.count]: item.count,
                        [t.revenue]: item.totalAmount,
                      }))}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Bar dataKey={t.count} fill="#0D3C4C" radius={[4, 4, 0, 0]} />
                        <Bar dataKey={t.revenue} fill="#4caf50" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Charts Row 2 */}
            <Grid container spacing={3}>
              <Grid item xs={12} md={8}>
                <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>{t.byDay}</Typography>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={summary.bookingsByDay || []}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="_id" tick={{ fontSize: 11 }} />
                        <YAxis />
                        <Tooltip formatter={(value: number) => `${value.toLocaleString()} ${currency}`} />
                        <Legend />
                        <Bar dataKey="revenue" name={t.revenue} fill="#0D3C4C" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, height: '100%' }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>{t.byPaymentMethod}</Typography>
                    <ResponsiveContainer width="100%" height={280}>
                      <PieChart>
                        <Pie
                          data={(summary.bookingsByPaymentMethod || []).map(item => ({
                            name: formatPaymentMethod(item._id),
                            value: item.totalPaid,
                          }))}
                          cx="50%"
                          cy="50%"
                          outerRadius={80}
                          dataKey="value"
                          label={({ name, value }) => `${name}: ${(value || 0).toLocaleString()}`}
                        >
                          {(summary.bookingsByPaymentMethod || []).map((_, index) => (
                            <Cell key={index} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value: number) => `${value.toLocaleString()} ${currency}`} />
                      </PieChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </>
        )}
        {!loading && !summary && <Alert severity="info">{t.noData}</Alert>}
      </TabPanel>

      {/* TAB 1 - Detailed */}
      <TabPanel value={tabValue} index={1}>
        {!loading && detailed && (
          <>
            <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>{t.client}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{t.wife}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{t.room}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{t.type}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{t.checkIn}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{t.checkOut}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{t.duration}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="right">{t.amount}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="right">{t.paid}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="right">{t.remaining}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{t.paymentStatus}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{t.method}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{t.status}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(detailed.bookings || []).map((row) => (
                      <TableRow key={row._id} hover>
                        <TableCell>{row.clientName}</TableCell>
                        <TableCell>{row.nomFemme || '-'}</TableCell>
                        <TableCell>{row.roomName || '-'}</TableCell>
                        <TableCell>{row.roomTypeName || '-'}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.checkInFormatted}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.checkOutFormatted}</TableCell>
                        <TableCell>{row.durationText}</TableCell>
                        <TableCell align="right">{(row.finalAmount || 0).toLocaleString()} {currency}</TableCell>
                        <TableCell align="right" sx={{ color: '#4caf50' }}>
                          {(row.paidAmount || 0).toLocaleString()} {currency}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{ color: (row.remainingAmount || 0) > 0 ? '#f44336' : 'inherit' }}
                        >
                          {(row.remainingAmount || 0).toLocaleString()} {currency}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={getPaymentStatusLabel(row.paymentStatus)}
                            size="small"
                            sx={{
                              backgroundColor: `${getPaymentStatusColor(row.paymentStatus)}20`,
                              color: getPaymentStatusColor(row.paymentStatus),
                              fontWeight: 600,
                              fontSize: '0.7rem',
                            }}
                          />
                        </TableCell>
                        <TableCell>{formatPaymentMethod(row.paymentMethod)}</TableCell>
                        <TableCell>
                          <Chip
                            label={translateStatus(row.status)}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: '0.7rem' }}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>

            {/* Pagination */}
            {detailed.totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <Pagination
                  count={detailed.totalPages}
                  page={detailed.currentPage}
                  onChange={(_, p) => setPage(p)}
                  color="primary"
                />
              </Box>
            )}
          </>
        )}
        {!loading && !detailed && <Alert severity="info">{t.noData}</Alert>}
      </TabPanel>

      {/* TAB 2 - By Room */}
      <TabPanel value={tabValue} index={2}>
        {!loading && byRoom.length > 0 && (
          <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>{t.roomNumber}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{t.roomType}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="center">{t.nbBookings}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">{t.totalRevenue}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">{t.totalPaid}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">{t.remaining}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">{t.avgAmount}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {byRoom.map((row, idx) => (
                    <TableRow key={idx} hover>
                      <TableCell>
                        {row.roomNumber || (
                          <Typography variant="body2" sx={{ color: '#9e9e9e', fontStyle: 'italic' }}>
                            {t.deletedRoom}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>{row.roomTypeName || '-'}</TableCell>
                      <TableCell align="center">{row.totalBookings}</TableCell>
                      <TableCell align="right">{(row.totalAmount || 0).toLocaleString()} {currency}</TableCell>
                      <TableCell align="right" sx={{ color: '#4caf50' }}>
                        {(row.totalPaid || 0).toLocaleString()} {currency}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ color: (row.totalRemaining || 0) > 0 ? '#f44336' : 'inherit' }}
                      >
                        {(row.totalRemaining || 0).toLocaleString()} {currency}
                      </TableCell>
                      <TableCell align="right">{(row.avgAmount || 0).toLocaleString()} {currency}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        )}
        {!loading && byRoom.length === 0 && <Alert severity="info">{t.noData}</Alert>}
      </TabPanel>
    </Container>
  );
};

export default Reports;
